import { z } from 'zod/v4'
import type { FlowerSubscriptionIndexFilters } from './flower-subscriptions.types'

// The filter and sort values `FlowerSubscriptionFilter` reads; `all` applies no filter.
export const flowerSubscriptionStatusValues = [
  'all',
  'needs_attention',
  'skipped',
  'active',
  'payment_failed',
  'fulfillment_unavailable',
  'payment_review',
  'cancelled',
] as const

export const flowerSubscriptionFrequencyValues = [
  'all',
  'weekly',
  'biweekly',
  'monthly',
  'every_3_months',
  'every_6_months',
  'yearly',
] as const

export const flowerSubscriptionFulfillmentTypeValues = ['all', 'pickup', 'delivery'] as const

export const flowerSubscriptionSortValues = [
  'attention',
  'id_asc',
  'id_desc',
  'customer_asc',
  'customer_desc',
  'organization_asc',
  'organization_desc',
  'package_asc',
  'package_desc',
  'tier_asc',
  'tier_desc',
  'status_asc',
  'status_desc',
  'frequency_asc',
  'frequency_desc',
  'next_fulfillment_asc',
  'next_fulfillment_desc',
  'next_charge_asc',
  'next_charge_desc',
  'created_asc',
  'created_desc',
] as const

export type FlowerSubscriptionStatusFilter = (typeof flowerSubscriptionStatusValues)[number]
export type FlowerSubscriptionFrequencyFilter = (typeof flowerSubscriptionFrequencyValues)[number]
export type FlowerSubscriptionFulfillmentTypeFilter =
  (typeof flowerSubscriptionFulfillmentTypeValues)[number]
export type FlowerSubscriptionSort = (typeof flowerSubscriptionSortValues)[number]

/** The index search params that always have a value. */
interface FlowerSubscriptionIndexSearchDefaults {
  status_filter: FlowerSubscriptionStatusFilter
  frequency_filter: FlowerSubscriptionFrequencyFilter
  fulfillment_type_filter: FlowerSubscriptionFulfillmentTypeFilter
  sort: FlowerSubscriptionSort
  page: number
  page_size: number
}

export const defaultFlowerSubscriptionIndexSearchParams: FlowerSubscriptionIndexSearchDefaults = {
  status_filter: 'all',
  frequency_filter: 'all',
  fulfillment_type_filter: 'all',
  sort: 'attention',
  page: 1,
  page_size: 20,
}

export const strippedFlowerSubscriptionIndexSearchParams: FlowerSubscriptionIndexSearchDefaults = {
  status_filter: defaultFlowerSubscriptionIndexSearchParams.status_filter,
  frequency_filter: defaultFlowerSubscriptionIndexSearchParams.frequency_filter,
  fulfillment_type_filter: defaultFlowerSubscriptionIndexSearchParams.fulfillment_type_filter,
  sort: defaultFlowerSubscriptionIndexSearchParams.sort,
  page: defaultFlowerSubscriptionIndexSearchParams.page,
  page_size: defaultFlowerSubscriptionIndexSearchParams.page_size,
}

export const flowerSubscriptionIndexSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  status_filter: z
    .enum(flowerSubscriptionStatusValues)
    .catch(defaultFlowerSubscriptionIndexSearchParams.status_filter),
  frequency_filter: z
    .enum(flowerSubscriptionFrequencyValues)
    .catch(defaultFlowerSubscriptionIndexSearchParams.frequency_filter),
  fulfillment_type_filter: z
    .enum(flowerSubscriptionFulfillmentTypeValues)
    .catch(defaultFlowerSubscriptionIndexSearchParams.fulfillment_type_filter),
  sort: z.enum(flowerSubscriptionSortValues).catch(defaultFlowerSubscriptionIndexSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultFlowerSubscriptionIndexSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultFlowerSubscriptionIndexSearchParams.page_size),
})

export type FlowerSubscriptionIndexSearch = z.output<typeof flowerSubscriptionIndexSearchSchema>

export function getFlowerSubscriptionIndexSearchParams(
  search: Partial<FlowerSubscriptionIndexSearch> = {}
) {
  return {
    ...defaultFlowerSubscriptionIndexSearchParams,
    ...search,
  }
}

export function getFlowerSubscriptionIndexFilters(
  search: Partial<FlowerSubscriptionIndexSearch> = {}
): FlowerSubscriptionIndexFilters {
  const params = getFlowerSubscriptionIndexSearchParams(search)
  const searchTerm = params.search?.trim()

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    status_filter: params.status_filter,
    frequency_filter: params.frequency_filter,
    fulfillment_type_filter: params.fulfillment_type_filter,
    sort: params.sort,
  }
}
